import { useMemo, useState } from 'react'

import { CellValueChangedEvent } from 'ag-grid-enterprise'
import { isAfter } from 'date-fns'
import { Download, Trash } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { renderToString } from 'react-dom/server'

import { getTMinus, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { Funds } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import {
  useDeleteNavEntryMutation,
  useDownloadSubscriptionRedemptionDataMutation,
  useUpdateNavEntryMutation,
} from '@/react-query/mutations/funds'
import { useNav } from '@/react-query/queries/funds/funds'

const floatFormatter = numberFormatter({ delimiter: true })

const getColDefs = (user: ReturnType<typeof useUserContext>) => {
  const hasWriteAccess = user.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_NAV_UPDATE],
  }))

  return createColDefs([
    {
      headerName: 'Nav Data',
      children: [
        { field: 'navid', hide: true },
        { field: 'navdate', hide: true, rowGroup: true },
        { field: 'fundname', width: 125, cellStyle: { textAlign: 'center' } },
        {
          field: 'nav',
          headerName: 'Soft NAV',
          width: 150,
          cellStyle: { textAlign: 'center' },
          editable: hasWriteAccess,
          valueFormatter: floatFormatter,
        },
        {
          field: 'ntnav',
          headerName: 'NT NAV',
          width: 150,
          cellStyle: { textAlign: 'center' },
          editable: hasWriteAccess,
          valueFormatter: floatFormatter,
        },
      ],
    },

    {
      headerName: 'Subscription / Redemption',
      children: [
        {
          field: 'predicted',
          width: 135,
          valueFormatter: floatFormatter,
          cellStyle: { textAlign: 'center' },
        },
        {
          field: 'confirmed',
          width: 135,
          valueFormatter: floatFormatter,
          cellStyle: { textAlign: 'center' },
        },
        {
          field: 'discrepancy',
          width: 125,
          valueFormatter: floatFormatter,
          headerName: 'Discr',
          cellStyle: { textAlign: 'center' },
        },
      ],
    },
  ])
}

export interface NavUpdatePageProps {}

export const NavUpdatePage: React.FC<NavUpdatePageProps> = ({}) => {
  const userContext = useUserContext()

  const [dates, setDates] = useState<DateRange>({
    from: getTMinus(15),
    to: getTMinus1(),
  })

  const params: Funds.GetNavParams = {
    st_dt: toISO8601(dates.from!),
    ed_dt: toISO8601(dates.to ?? dates.from!),
  }

  const navQuery = useNav(params)
  const deleteNavEntryMutation = useDeleteNavEntryMutation(params)
  const updateNavEntryMutation = useUpdateNavEntryMutation(params)
  const downloadSRDataMutation = useDownloadSubscriptionRedemptionDataMutation()

  const isUpdating = deleteNavEntryMutation.isPending || updateNavEntryMutation.isPending

  const [deletingNavData, setDeletingNavData] = useState<{
    fund_name: string
    value_date: string
  } | null>(null)

  const hasWriteAccess = userContext.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_NAV_UPDATE],
  }))

  const data = useMemo(() => {
    return navQuery.data ?? []
  }, [navQuery.data])

  const { withConfirmation, Dialog: ConfirmDeleteModal } = useConfirmModal({
    title: `Delete NAV entry`,
    description: `Do you really want to delete NAV entry for ${deletingNavData?.fund_name} on ${deletingNavData?.value_date}?`,
  })

  const getContextMenuItems = createContextMenu<(typeof data)[number]>((params) => {
    const fund_name = params.node!.data?.fund_name!
    const value_date = params.node!.data?.navdate!

    const items: CustomMenuItem[] = [
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'export',
    ]

    if (hasWriteAccess && !params.node?.group) {
      items.unshift('separator')
      items.unshift({
        name: `Delete NAV - ${fund_name} - ${value_date}`,
        action: () => {
          setDeletingNavData({ fund_name, value_date })
          withConfirmation(() =>
            deleteNavEntryMutation.mutateAsync({
              fund_name,
              value_date,
            }),
          )
        },
        cssClasses: ['bg-red-400/10!', 'hover:bg-red-400/20!'],
        icon: renderToString(<Trash size={16} />),
      })
    }

    return items
  })

  return (
    <Page metadata={{ title: 'NAV Update' }} permissions={OPERATIONS.NAV_UPDATE.permissions}>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <DateRangePicker
            size="sm"
            date={dates}
            setDate={setDates}
            disabled={(date) => isAfter(date, new Date())}
          />
          <LoadingButton
            size="sm"
            variant={'outline'}
            icon={<Download />}
            onClick={() => downloadSRDataMutation.mutateAsync()}
          >
            Download S/R (past 10 days)
          </LoadingButton>
        </div>
      </HeaderConfigBar>
      <ConfirmDeleteModal />
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={navQuery} title="NAV Update" />
        <WidgetContent loading={navQuery?.isLoading}>
          <CustomAgGrid
            updating={isUpdating}
            domLayout={'normal'}
            onCellValueChanged={(params: CellValueChangedEvent) => {
              return updateNavEntryMutation.mutateAsync({
                newValue: params.newValue,
                oldValue: params.oldValue,
                rowData: params.data,
                updatedField: params.colDef.field!,
              })
            }}
            getContextMenuItems={getContextMenuItems}
            rowData={data}
            columnDefs={getColDefs(userContext)}
          />
        </WidgetContent>
      </Widget>
    </Page>
  )
}
